export const createPasswordValidator = (config) => {
    if (!config) {
        return () => ({
            isValid: true,
            strength: 0,
            errors: [],
            warnings: [],
            requirements: [],
            configUsed: null
        });
    }
    return (password: string = '') => {
        const errors = [];
        const warnings = [];
        const requirements = [];

        // Length validation
        if (config.length) {
            if (password.length < config.minLength) {
                errors.push(`رمز عبور باید حداقل ${config.minLength} کاراکتر باشد`);
            }
            if (password.length > config.maxLength) {
                errors.push(`رمز عبور نباید بیشتر از ${config.maxLength} کاراکتر باشد`);
            }
            requirements.push(`طول رمز عبور بین ${config.minLength} تا ${config.maxLength} کاراکتر`);
        }

        // Character characteristics
        if (config.characterCharacteristics) {
            const checks = [];

            if (config.digitCharacter) {
                const digitCount = (password.match(/\d/g) || []).length;
                if (digitCount < config.numberOfDigits) {
                    errors.push(`رمز عبور باید حداقل ${config.numberOfDigits} عدد داشته باشد`);
                }
                checks.push(`${config.numberOfDigits} عدد`);
            }

            if (config.nonAlphanumericCharacter) {
                const nonAlphaNumCount = (password.match(/[^a-zA-Z0-9]/g) || []).length;
                if (nonAlphaNumCount < config.numberOfNonAlphanumerics) {
                    errors.push(`رمز عبور باید حداقل ${config.numberOfNonAlphanumerics} کاراکتر غیر الفبایی داشته باشد`);
                }
                checks.push(`${config.numberOfNonAlphanumerics} کاراکتر خاص`);
            }

            if (config.uppercaseCharacter) {
                const upperCount = (password.match(/[A-Z]/g) || []).length;
                if (upperCount < config.numberOfUppercases) {
                    errors.push(`رمز عبور باید حداقل ${config.numberOfUppercases} حرف بزرگ داشته باشد`);
                }
                checks.push(`${config.numberOfUppercases} حرف بزرگ`);
            }

            if (config.lowercaseCharacter) {
                const lowerCount = (password.match(/[a-z]/g) || []).length;
                if (lowerCount < config.numberOfLowercases) {
                    errors.push(`رمز عبور باید حداقل ${config.numberOfLowercases} حرف کوچک داشته باشد`);
                }
                checks.push(`${config.numberOfLowercases} حرف کوچک`);
            }

            if (checks.length > 0) {
                requirements.push(`شامل: ${checks.join('، ')}`);
            }
        }

        // Sequence checks
        const checkSequence = (sequenceType, sequenceLength, wrap) => {
            const sequences = {
                alphabetical: 'abcdefghijklmnopqrstuvwxyz',
                numerical: '0123456789',
                qwerty: 'qwertyuiopasdfghjklzxcvbnm'
            };

            const sequence = sequences[sequenceType];
            let found = false;

            for (let i = 0; i <= sequence.length - sequenceLength; i++) {
                const seq = sequence.substr(i, sequenceLength);
                if (password.toLowerCase().includes(seq)) {
                    found = true;
                    break;
                }

                if (wrap && i === sequence.length - sequenceLength) {
                    const wrappedSeq = sequence.substr(i) + sequence.substr(0, sequenceLength - (sequence.length - i));
                    if (password.toLowerCase().includes(wrappedSeq)) {
                        found = true;
                        break;
                    }
                }
            }

            return found;
        };

        if (config.alphabeticalSequence) {
            if (checkSequence('alphabetical', config.alphabeticalSequenceLength, config.wrapAlphabeticalSequence)) {
                warnings.push('رمز عبور شامل دنباله الفبایی است');
            }
        }

        if (config.numericalSequence) {
            if (checkSequence('numerical', config.numericalSequenceLength, config.wrapNumericalSequence)) {
                warnings.push('رمز عبور شامل دنباله عددی است');
            }
        }

        if (config.qwertySequence) {
            if (checkSequence('qwerty', config.qwertySequenceLength, config.wrapQwertySequence)) {
                warnings.push('رمز عبور شامل دنباله صفحه کلید است');
            }
        }

        // Repeated characters
        if (config.repeatCharacterRegex) {
            const regex = new RegExp(`(.)\\1{${config.repeatCharacterSequenceLength - 1},}`);
            if (regex.test(password)) {
                warnings.push(`رمز عبور شامل ${config.repeatCharacterSequenceLength} یا بیشتر کاراکتر تکراری است`);
            }
        }

        // Whitespace check
        if (config.whitespace && /\s/.test(password)) {
            warnings.push('رمز عبور شامل فاصله (Space) است');
        }

        // Calculate strength score (0-100)
        let strength = 0;
        if (errors.length === 0) {
            // Base score for meeting requirements
            strength = 50;

            // Bonus for length
            const lengthPercent = Math.min(100, (password.length - config.minLength) / (config.maxLength - config.minLength) * 100);
            strength += lengthPercent * 0.3;

            // Bonus for character variety
            const varietyScore = (
                (config.digitCharacter && /\d/.test(password) ? 5 : 0) +
                (config.nonAlphanumericCharacter && /[^a-zA-Z0-9]/.test(password) ? 5 : 0) +
                (config.uppercaseCharacter && /[A-Z]/.test(password) ? 5 : 0) +
                (config.lowercaseCharacter && /[a-z]/.test(password) ? 5 : 0)
            );
            strength += varietyScore;

            // Penalty for sequences and repeats
            strength -= warnings.length * 5;

            // Ensure score is within bounds
            strength = Math.max(0, Math.min(100, strength));
        }

        return {
            isValid: errors.length === 0,
            strength,
            errors,
            warnings,
            requirements,
            configUsed: config
        };
    };
};